import React from "react";
import {
  Star, ExternalLink, ShieldCheck, MessageSquare, ThumbsUp, Sparkles,
} from "lucide-react";

const TRUSTPILOT_URL = "https://www.trustpilot.com/review/dasabo.com";
const TRUSTPILOT_PROFILE_URL = "https://www.trustpilot.com/review/dasabo.com";

const BENEFITS = [
  {
    icon: MessageSquare,
    title: "La tua voce conta davvero",
    body: "Ogni review su Trustpilot viene letta da chi sta valutando Autofattura. Un tuo commento può cambiare il modo in cui altre partite IVA italiane approcciano la contabilità delle spese estere.",
  },
  {
    icon: ThumbsUp,
    title: "Aiutaci a crescere con dati veri",
    body: "Il rating pubblico di Trustpilot è indipendente e verificabile. Un feedback sincero ci aiuta a prioritizzare le cose che funzionano meno e a raddoppiare su quelle che ami.",
  },
  {
    icon: ShieldCheck,
    title: "2 minuti, zero registrazione noiosa",
    body: "Basta il login Google/Facebook o la tua email. Trustpilot non ti chiederà password, PEC o P.IVA — solo la tua esperienza reale con il servizio.",
  },
];

export default function AccountReviews() {
  return (
    <div className="space-y-6" data-testid="account-reviews">
      {/* HERO CTA CARD */}
      <section
        className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-white via-emerald-50/60 to-white p-6 sm:p-8"
        data-testid="trustpilot-hero"
      >
        {/* Decorative stars background */}
        <div aria-hidden="true" className="absolute -top-6 -right-6 opacity-[0.08]">
          <Star className="w-40 h-40 text-emerald-600 fill-current" />
        </div>

        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white grid place-items-center shrink-0 shadow-lg shadow-emerald-500/30">
            <Star className="w-6 h-6 fill-current" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] uppercase tracking-wider font-bold mb-2">
              <ShieldCheck className="w-3 h-3" /> Recensioni verificate da Trustpilot
            </div>
            <h2 className="font-display font-extrabold text-xl sm:text-2xl text-[#0A0A0A] leading-tight">
              Racconta la tua esperienza con Autofattura
            </h2>
            <p className="mt-2 text-sm sm:text-base text-zinc-700 leading-relaxed max-w-2xl">
              Le recensioni le lasciamo dove contano davvero: <strong>Trustpilot</strong>, la piattaforma
              indipendente più autorevole in Europa. Se ti abbiamo salvato ore di lavoro o se qualcosa
              non ti torna, scrivilo lì — noi leggiamo tutto e rispondiamo pubblicamente.
            </p>

            {/* Star preview row */}
            <div className="mt-4 flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-5 h-5 fill-emerald-500 text-emerald-500" />
              ))}
              <span className="ml-1 text-xs text-zinc-500">
                Rating pubblico, indipendente, non censurabile
              </span>
            </div>

            {/* CTAs */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trustpilot-write-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition-colors shadow-md shadow-emerald-600/20"
              >
                <Sparkles className="w-4 h-4" />
                Lascia una recensione su Trustpilot
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a
                href={TRUSTPILOT_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="trustpilot-profile-link"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[#0A0A0A] text-sm font-semibold hover:bg-emerald-100/50 transition-colors"
              >
                Leggi le recensioni degli altri
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BENEFITS */}
      <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
        <h3 className="font-display font-semibold text-lg text-[#0A0A0A] mb-1">
          Perché su Trustpilot e non qui dentro
        </h3>
        <p className="text-sm text-zinc-500 mb-5 max-w-2xl">
          Un feedback interno saremmo noi a leggerlo e archiviarlo. Su Trustpilot lo vedono tutti i
          futuri clienti, per sempre. È un patto di trasparenza.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {BENEFITS.map(({ icon: Icon, title, body }, idx) => (
            <div
              key={title}
              data-testid={`trustpilot-benefit-${idx}`}
              className="rounded-xl border border-[#E8E4D9] bg-[#FAF9F6] p-4"
            >
              <div className="w-9 h-9 rounded-lg bg-white border border-[#E8E4D9] grid place-items-center mb-3">
                <Icon className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="font-display font-bold text-sm text-[#0A0A0A] mb-1.5">{title}</div>
              <p className="text-xs text-zinc-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-[#E8E4D9] flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <p className="text-sm text-zinc-600">
            Fatto? <strong className="text-[#0A0A0A]">Grazie</strong>, davvero — leggiamo ogni review entro 24h.
          </p>
          <a
            href={TRUSTPILOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trustpilot-write-cta-secondary"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0A0A] text-white font-semibold text-xs hover:bg-emerald-600 transition-colors"
          >
            <Star className="w-3.5 h-3.5 fill-current" />
            Apri Trustpilot
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>
    </div>
  );
}
